import { useEffect, useMemo, useState } from 'react';
import {
  BarChart3,
  Bell,
  CalendarRange,
  ChevronDown,
  CircleHelp,
  Database,
  FileSpreadsheet,
  Gauge,
  LayoutDashboard,
  Menu,
  PanelLeftClose,
  Settings2,
  ShieldCheck,
  Target,
  Upload,
  UsersRound,
  X,
} from 'lucide-react';
import TargetDrawer from './components/TargetDrawer';
import UploadModal from './components/UploadModal';
import { createDemoDataset } from './data/demo';
import { ALL, filterRecords, getOptions, periodBounds, quickRange, summarize } from './lib/analytics';
import { importExcel, type ImportProgress } from './lib/excel';
import { formatDate, formatNumber } from './lib/format';
import { clearDataset, loadDataset, loadTargets, saveDataset, saveTargets } from './lib/storage';
import DataView from './views/DataView';
import KpiView from './views/KpiView';
import OverviewView from './views/OverviewView';
import PenetrationView from './views/PenetrationView';
import TerminalView from './views/TerminalView';
import type { DashboardDataset, DashboardFilters, PeriodMode, TargetConfig, ViewKey } from './types';

const demoDataset = createDemoDataset();

const defaultTargets: TargetConfig = {
  annualSalesTarget: 18_000_000,
  annualProductQuantityTarget: 1_800,
  activeTerminalTarget: 120,
  targetProduct: 'Phesgo Vial Maintenance dose(10ml) 1',
  updatedAt: '2026-08-17T08:00:00.000Z',
};

const navItems: Array<{ key: ViewKey; label: string; helper: string; icon: typeof LayoutDashboard }> = [
  { key: 'overview', label: '经营总览', helper: '核心指标', icon: LayoutDashboard },
  { key: 'penetration', label: '单品渗透', helper: '覆盖趋势', icon: Target },
  { key: 'kpi', label: 'KPI 跟进', helper: '目标达成', icon: Gauge },
  { key: 'terminal', label: '终端分析', helper: '大类趋势', icon: UsersRound },
  { key: 'data', label: '数据与下载', helper: '明细管理', icon: Database },
];

const viewTitles: Record<ViewKey, { title: string; subtitle: string }> = {
  overview: { title: '经营总览', subtitle: '关键结果、目标达成与经营信号' },
  penetration: { title: '单品渗透', subtitle: '看清重点单品的终端覆盖与增长机会' },
  kpi: { title: 'KPI 跟进', subtitle: '三个核心指标按月、季、年持续追踪' },
  terminal: { title: '终端分析', subtitle: '按终端大类拆解结构、趋势和贡献' },
  data: { title: '数据与下载', subtitle: '管理本地数据并下载固定维度分析' },
};

const initialFilters = (dataset: DashboardDataset): DashboardFilters => {
  const range = quickRange(dataset.meta.dateMax, 'month', dataset.meta.dateMin);
  return {
    ...range,
    province: ALL,
    city: ALL,
    brand: ALL,
    product: ALL,
    flowType: ALL,
  };
};

type RangeKey = 'all' | '30d' | PeriodMode | 'custom';

export default function App() {
  const [dataset, setDataset] = useState<DashboardDataset>(demoDataset);
  const [filters, setFilters] = useState<DashboardFilters>(() => initialFilters(demoDataset));
  const [targets, setTargets] = useState<TargetConfig>(() => loadTargets(defaultTargets));
  const [view, setView] = useState<ViewKey>('overview');
  const [periodMode, setPeriodMode] = useState<PeriodMode>('month');
  const [rangeKey, setRangeKey] = useState<RangeKey>('month');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [targetOpen, setTargetOpen] = useState(false);
  const [importProgress, setImportProgress] = useState<ImportProgress | null>(null);
  const [importError, setImportError] = useState('');
  const [toast, setToast] = useState('');
  const [mobileNav, setMobileNav] = useState(false);
  const [sidebarCompact, setSidebarCompact] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    void loadDataset()
      .then((saved) => {
        if (saved?.records?.length) {
          setDataset(saved);
          setFilters(initialFilters(saved));
        }
      })
      .finally(() => setHydrated(true));
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(''), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const products = useMemo(() => getOptions(dataset.records, 'product'), [dataset.records]);
  const provinces = useMemo(() => getOptions(dataset.records, 'province'), [dataset.records]);
  const brands = useMemo(() => getOptions(dataset.records, 'brand'), [dataset.records]);
  const flows = useMemo(() => getOptions(dataset.records, 'flowType'), [dataset.records]);
  const cities = useMemo(
    () => getOptions(dataset.records.filter((record) => filters.province === ALL || record.province === filters.province), 'city'),
    [dataset.records, filters.province],
  );

  useEffect(() => {
    if (!products.length || products.includes(targets.targetProduct)) return;
    const counts = new Map<string, number>();
    dataset.records.forEach((record) => counts.set(record.product, (counts.get(record.product) ?? 0) + 1));
    const targetProduct = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? products[0];
    const next = { ...targets, targetProduct, updatedAt: new Date().toISOString() };
    setTargets(next);
    saveTargets(next);
  }, [dataset.records, products, targets]);

  const visibleRecords = useMemo(() => filterRecords(dataset.records, filters), [dataset.records, filters]);
  const summary = useMemo(() => summarize(dataset.records, filters, targets.targetProduct), [dataset.records, filters, targets.targetProduct]);
  const dateLabel = `${filters.startDate}_${filters.endDate}`;
  const activeDimensionCount = [filters.province, filters.city, filters.brand, filters.product, filters.flowType].filter((value) => value !== ALL).length;

  const applyRange = (key: Exclude<RangeKey, 'custom'>) => {
    const range = quickRange(dataset.meta.dateMax, key, dataset.meta.dateMin);
    setFilters((current) => ({ ...current, ...range }));
    setRangeKey(key);
    if (key === 'month' || key === 'quarter' || key === 'year') setPeriodMode(key);
  };

  const applyPeriod = (mode: PeriodMode) => {
    const range = periodBounds(dataset.meta.dateMax, mode);
    setPeriodMode(mode);
    setRangeKey(mode);
    setFilters((current) => ({
      ...current,
      startDate: range.startDate < dataset.meta.dateMin ? dataset.meta.dateMin : range.startDate,
      endDate: dataset.meta.dateMax,
    }));
  };

  const updateFilter = (key: keyof DashboardFilters, value: string) => {
    setFilters((current) => ({
      ...current,
      [key]: value,
      ...(key === 'province' ? { city: ALL } : {}),
    }));
    if (key === 'startDate' || key === 'endDate') setRangeKey('custom');
  };

  const resetDimensions = () => setFilters((current) => ({ ...current, province: ALL, city: ALL, brand: ALL, product: ALL, flowType: ALL }));

  const handleFile = async (file: File) => {
    setImportError('');
    setImportProgress({ progress: 2, message: '准备导入' });
    try {
      const imported = await importExcel(file, setImportProgress);
      await saveDataset(imported);
      setDataset(imported);
      setFilters(initialFilters(imported));
      setRangeKey('month');
      setPeriodMode('month');
      setImportProgress({ progress: 100, message: `已导入 ${formatNumber(imported.meta.rowCount)} 条记录` });
      window.setTimeout(() => {
        setUploadOpen(false);
        setImportProgress(null);
        setToast(`数据已更新：${imported.meta.fileName}`);
      }, 650);
    } catch (error) {
      setImportProgress(null);
      setImportError(error instanceof Error ? error.message : '导入失败，请检查文件');
    }
  };

  const saveTargetConfig = (next: TargetConfig) => {
    setTargets(next);
    saveTargets(next);
    setTargetOpen(false);
    setToast('目标已保存，所有图表已重新计算');
  };

  const restoreDemo = async () => {
    if (!window.confirm('确定清除当前浏览器中保存的业务数据并恢复演示数据吗？')) return;
    await clearDataset();
    setDataset(demoDataset);
    setFilters(initialFilters(demoDataset));
    setRangeKey('month');
    setPeriodMode('month');
    setToast('本地业务数据已清除，现已恢复演示数据');
  };

  const selectView = (next: ViewKey) => {
    setView(next);
    setMobileNav(false);
  };

  return (
    <div className={`app-shell ${sidebarCompact ? 'sidebar-compact' : ''}`}>
      <button className={`mobile-scrim ${mobileNav ? 'show' : ''}`} type="button" onClick={() => setMobileNav(false)} aria-label="关闭导航" />
      <aside className={`sidebar ${mobileNav ? 'mobile-open' : ''}`}>
        <div className="brand-block">
          <span className="brand-symbol"><BarChart3 size={21} /></span>
          <div className="brand-copy"><strong>业务增长看板</strong><span>BUSINESS PULSE</span></div>
          <button className="icon-button sidebar-collapse" type="button" onClick={() => setSidebarCompact((current) => !current)} aria-label="收起侧栏"><PanelLeftClose size={17} /></button>
          <button className="icon-button mobile-nav-close" type="button" onClick={() => setMobileNav(false)} aria-label="关闭导航"><X size={18} /></button>
        </div>

        <nav className="sidebar-nav" aria-label="主导航">
          <span className="nav-section-label">分析中心</span>
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.key} type="button" className={view === item.key ? 'active' : ''} onClick={() => selectView(item.key)} title={item.label}>
                <span className="nav-icon"><Icon size={18} /></span>
                <span className="nav-copy"><strong>{item.label}</strong><small>{item.helper}</small></span>
              </button>
            );
          })}
        </nav>

        <div className="sidebar-bottom">
          <div className="privacy-chip"><ShieldCheck size={17} /><span><strong>本地隐私模式</strong><small>Excel 不离开浏览器</small></span></div>
          <div className="sidebar-user">
            <span className="avatar">业</span>
            <span className="user-copy"><strong>业务分析端</strong><small>无需登录 · 本机数据</small></span>
            <ChevronDown size={15} />
          </div>
        </div>
      </aside>

      <main className="main-shell">
        <header className="topbar">
          <div className="topbar-title">
            <button className="icon-button mobile-menu" type="button" onClick={() => setMobileNav(true)} aria-label="打开导航"><Menu size={19} /></button>
            <div><h1>{viewTitles[view].title}</h1><p>{viewTitles[view].subtitle}</p></div>
          </div>
          <div className="topbar-actions">
            <span className={`dataset-state ${dataset.meta.demo ? 'demo' : 'live'}`}><i />{dataset.meta.demo ? '演示模式' : '业务数据已就绪'}</span>
            <button className="icon-button hide-mobile" type="button" title="使用说明" aria-label="使用说明"><CircleHelp size={18} /></button>
            <button className="icon-button hide-mobile" type="button" title="消息" aria-label="消息"><Bell size={18} /></button>
            <button className="secondary-button hide-mobile" type="button" onClick={() => setTargetOpen(true)}><Settings2 size={16} /> 目标设置</button>
            <button className="primary-button" type="button" onClick={() => { setImportError(''); setUploadOpen(true); }}><Upload size={17} /> 上传 Excel</button>
          </div>
        </header>

        <section className="filter-shell">
          <div className="quick-ranges" aria-label="时间范围">
            <CalendarRange size={17} />
            {([
              ['month', '本月'], ['quarter', '本季'], ['year', '本年'], ['30d', '近30天'], ['all', '全部'],
            ] as Array<[Exclude<RangeKey, 'custom'>, string]>).map(([key, label]) => (
              <button key={key} type="button" className={rangeKey === key ? 'active' : ''} onClick={() => applyRange(key)}>{label}</button>
            ))}
          </div>
          <div className="date-inputs">
            <input aria-label="开始日期" type="date" min={dataset.meta.dateMin} max={filters.endDate} value={filters.startDate} onChange={(event) => updateFilter('startDate', event.target.value)} />
            <span>至</span>
            <input aria-label="结束日期" type="date" min={filters.startDate} max={dataset.meta.dateMax} value={filters.endDate} onChange={(event) => updateFilter('endDate', event.target.value)} />
          </div>
          <div className="filter-divider" />
          <div className="dimension-filters">
            <FilterSelect label="省份" value={filters.province} options={provinces} onChange={(value) => updateFilter('province', value)} />
            <FilterSelect label="城市" value={filters.city} options={cities} onChange={(value) => updateFilter('city', value)} />
            <FilterSelect label="品牌" value={filters.brand} options={brands} onChange={(value) => updateFilter('brand', value)} />
            <FilterSelect label="产品" value={filters.product} options={products} onChange={(value) => updateFilter('product', value)} wide />
            <FilterSelect label="流向" value={filters.flowType} options={flows} onChange={(value) => updateFilter('flowType', value)} />
          </div>
          {activeDimensionCount > 0 && <button className="clear-filters" type="button" onClick={resetDimensions}>清除 {activeDimensionCount} 项</button>}
        </section>

        <div className="data-context-bar">
          <div><FileSpreadsheet size={16} /><strong>{dataset.meta.fileName}</strong><span>{formatDate(filters.startDate)} — {formatDate(filters.endDate)}</span></div>
          <span>当前 {formatNumber(visibleRecords.length)} / {formatNumber(dataset.meta.rowCount)} 条</span>
        </div>

        <div className={`main-content ${!hydrated ? 'is-hydrating' : ''}`}>
          {view === 'overview' && <OverviewView records={visibleRecords} summary={summary} targets={targets} periodMode={periodMode} onPeriodMode={applyPeriod} onOpenTargets={() => setTargetOpen(true)} />}
          {view === 'penetration' && <PenetrationView records={visibleRecords} summary={summary} targetProduct={targets.targetProduct} dateLabel={dateLabel} onOpenTargets={() => setTargetOpen(true)} />}
          {view === 'kpi' && <KpiView records={visibleRecords} summary={summary} targets={targets} periodMode={periodMode} dateLabel={dateLabel} onPeriodMode={applyPeriod} onOpenTargets={() => setTargetOpen(true)} />}
          {view === 'terminal' && <TerminalView records={visibleRecords} targetProduct={targets.targetProduct} dateLabel={dateLabel} />}
          {view === 'data' && <DataView dataset={dataset} records={visibleRecords} targets={targets} dateLabel={dateLabel} onUpload={() => setUploadOpen(true)} onRestoreDemo={() => void restoreDemo()} />}
          {!visibleRecords.length && view !== 'data' && (
            <section className="empty-state"><Database size={28} /><h2>当前筛选没有数据</h2><p>请放宽日期或清除部分维度筛选。</p><button className="secondary-button" type="button" onClick={resetDimensions}>清除维度筛选</button></section>
          )}
        </div>
      </main>

      <UploadModal open={uploadOpen} progress={importProgress} error={importError} onClose={() => { if (!importProgress) setUploadOpen(false); }} onFile={(file) => void handleFile(file)} />
      <TargetDrawer open={targetOpen} targets={targets} products={products} onClose={() => setTargetOpen(false)} onSave={saveTargetConfig} />
      {toast && <div className="toast" role="status"><span><ShieldCheck size={17} /></span>{toast}</div>}
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  wide?: boolean;
}

function FilterSelect({ label, value, options, onChange, wide }: FilterSelectProps) {
  return (
    <label className={`filter-select ${wide ? 'filter-wide' : ''}`}>
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value={ALL}>全部</option>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}
